<template>
  <div
    v-if="canRerun"
    class="d-flex justify-end mb-4"
    data-testid="draw-rerun-action"
  >
    <SoButton
      title="Rerun selection to replace rejected winners"
      type="primary"
      data-testid="rerun-selection-btn"
      @click="showRerunDialog = true"
    />

    <SoDialog
      v-model="showRerunDialog"
      card-title="Replace rejected winners?"
      :max-width="480"
      data-testid="rerun-selection-dialog"
    >
      <p>
        This re-runs selection to fill {{ refillableSlots }} rejected
        {{ refillableSlots === 1 ? "slot" : "slots" }}. Existing winners are
        kept — only rejected slots are topped up.
      </p>
      <div class="d-flex justify-end mt-4">
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          @click="showRerunDialog = false"
        />
        <SoButton
          title="Rerun selection"
          type="primary"
          large
          :loading="reprocessMutation.isPending"
          data-testid="rerun-selection-confirm-btn"
          @click="confirmRerun"
        />
      </div>
    </SoDialog>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import { PrizeDrawStatus } from "@/api/backofficeRest/generated/model"
  import { IDrawWinner } from "@/models/operations/draw/drawWinner.types"
  import { IDrawReward } from "@/models/operations/draw/drawReward.types"
  import { unfilledRejectedSlots } from "@/helpers/drawWinnerHelper"

  export default defineComponent({
    components: { SoButton, SoDialog },
    props: {
      drawId: {
        type: String,
        required: true,
      },
      status: {
        type: String as PropType<PrizeDrawStatus | null>,
        default: null,
      },
      winners: {
        type: Array as PropType<IDrawWinner[]>,
        default: () => [],
      },
      rewards: {
        type: Array as PropType<IDrawReward[]>,
        default: () => [],
      },
    },
    emits: ["changed"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        reprocessMutation: LoyaltyMutator.reprocessWinners(),
        showRerunDialog: false,
      }
    },
    computed: {
      // Rejected slots a rerun can fill; shared with OpsNotifyWinnersAction.
      refillableSlots(): number {
        return unfilledRejectedSlots(this.winners, this.rewards)
      },
      // COMPLETED qualifies too — a calculated draw past its completion date.
      canRerun(): boolean {
        return (
          (this.status === PrizeDrawStatus.WINNERS_CALCULATED ||
            this.status === PrizeDrawStatus.COMPLETED) &&
          this.refillableSlots > 0
        )
      },
    },
    methods: {
      async confirmRerun() {
        try {
          await this.reprocessMutation.mutateAsync({ drawId: this.drawId })
        } catch (error) {
          errorHandler.showCaughtError(
            error,
            "Failed to rerun selection. Please try again.",
          )
          return
        }
        this.snackbar.addSuccess("Rejected slots topped up")
        this.showRerunDialog = false
        this.$emit("changed")
      },
    },
  })
</script>
